import { useState } from "react";
import { Pencil } from "lucide-react";
import { API_ROLE } from "../../../../config/Endpoints.js";
import { Button, Modal, ResponsiveDataTable, Select, Spinner, type ResponsiveColumn } from "../../../components/ui/index.ts";
import { ApiError, GENERIC_ERROR, deleteApi, postApi, putApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { RoleFormModal } from "../role-form-modal.tsx";
import { useRoleManagementQuery } from "../use-role-management-query.ts";
import { roleLabel, type ListResponse, type Role } from "../types.ts";
import styles from "../role-management.module.css";

type RoleRow = Role & { id: string } & Record<string, unknown>;

export function RoleListTab() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [roleId, setRoleId] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState<Role | null | undefined>();
  const [deleting, setDeleting] = useState<Role | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const roles = useRoleManagementQuery<ListResponse<Role>>(API_ROLE, refreshKey);
  const query = new URLSearchParams({
    pageSize: String(pageSize),
    // The table uses one-based pages; the API uses zero-based pages.
    page: String(page - 1),
  });
  if (roleId) query.set("roleId", roleId);
  const { data, loading, error } = useRoleManagementQuery<ListResponse<Role>>(
    `${API_ROLE}?${query.toString()}`,
    refreshKey,
  );
  const rows: RoleRow[] = (data?.data ?? [])
    .filter((role) => roleLabel(role).toLowerCase().includes(search.trim().toLowerCase()))
    .map((role) => ({ ...role, id: String(role.roleId) }));

  const save = async (values: { displayName: string; description: string }) => {
    setSubmitting(true);
    try {
      if (editing) await putApi(`${API_ROLE}/${editing.roleId}`, accessToken(), values);
      else await postApi(API_ROLE, accessToken(), values);
      setEditing(undefined);
      setRefreshKey((key) => key + 1);
      showSuccess(editing ? "Role updated successfully." : "Role created successfully.");
    } catch (cause) {
      showError(cause instanceof ApiError ? cause.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!deleting || submitting) return;
    setSubmitting(true);
    try {
      await deleteApi(`${API_ROLE}/${deleting.roleId}`, accessToken());
      if (roleId === String(deleting.roleId)) {
        setRoleId("");
        setPage(1);
      } else if (data?.data?.length === 1 && page > 1) {
        setPage(page - 1);
      }
      setDeleting(null);
      setRefreshKey((key) => key + 1);
      showSuccess("Role deleted successfully.");
    } catch (cause) {
      showError(cause instanceof ApiError ? cause.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ResponsiveColumn<RoleRow>[] = [
    { id: "title", header: "Title", render: roleLabel },
    { id: "description", header: "Description", accessor: "description" },
    { id: "actions", header: "Actions", render: (role) => (
      <div className={styles.actions}>
        <button type="button" className={styles.editAction} onClick={() => setEditing(role)} aria-label={`Edit ${roleLabel(role)}`}><Pencil size={14} aria-hidden />Edit</button>
        <button type="button" className={styles.deleteAction} onClick={() => setDeleting(role)} aria-label={`Delete ${roleLabel(role)}`}>Delete</button>
      </div>
    ) },
  ];

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.field}>
          <Select
            id="role-list-filter"
            label="Role"
            placeholder="Select Role"
            searchable
            isClearable
            value={roleId}
            options={(roles.data?.data ?? []).map((role) => ({
              value: String(role.roleId),
              label: roleLabel(role),
            }))}
            disabled={roles.loading || submitting}
            onChange={(value) => {
              setRoleId(value);
              setSearch("");
              setPage(1);
            }}
          />
        </div>
        <div className={styles.actions}><Button variant="accent" onClick={() => setEditing(null)}>+ Add Role</Button></div>
      </div>
      {error ? null : loading ? <Spinner /> : (
        <ResponsiveDataTable<RoleRow> aria-label="Role list" serialNumber columns={columns} data={rows} pagination page={page} pageSize={pageSize} totalItems={data?.totalRoles ?? 0} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} pageSizeOptions={[5, 10, 20, 30, 40]} />
      )}
      {editing !== undefined && <RoleFormModal role={editing} submitting={submitting} onClose={() => setEditing(undefined)} onSubmit={save} />}
      <Modal open={Boolean(deleting)} onClose={() => { if (!submitting) setDeleting(null); }} title="Delete Role" size="md" footer={
        <div className={styles.actions}>
          <Button variant="light-grey" disabled={submitting} onClick={() => setDeleting(null)}>Cancel</Button>
          <Button variant="danger" disabled={submitting} onClick={() => void remove()}>{submitting ? "Deleting..." : "Delete Role"}</Button>
        </div>
      }>
        <p>Are you sure you want to delete {deleting ? roleLabel(deleting) : "this role"}?</p>
      </Modal>
    </div>
  );
}
